/**
 * Adaptive polling hook.
 *
 *   - Calls `tick` immediately on mount, then schedules the next call using
 *     the delay returned by `getDelayMs` after the previous tick resolves.
 *     Re-evaluating after each tick is what lets callers slow down once
 *     all visible work is terminal and speed back up if a new run appears.
 *   - Pauses while the tab is hidden and runs an immediate catch-up tick on
 *     visibility return so the user doesn't see a stale row when they come
 *     back to the tab.
 *   - Reschedules on `deps` change (the typical use is `[runId]` on detail
 *     pages so navigating between runs starts a fresh poll).
 *
 * Set `getDelayMs` to `null` to stop polling — useful if a run reaches a
 * truly terminal state and you want zero further requests.
 */

import { useEffect, useRef } from "react";

export function usePoll(
  tick: () => Promise<void> | void,
  getDelayMs: () => number | null,
  deps: ReadonlyArray<unknown> = [],
): void {
  const tickRef = useRef(tick);
  const delayRef = useRef(getDelayMs);
  tickRef.current = tick;
  delayRef.current = getDelayMs;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function run(): Promise<void> {
      if (cancelled) return;
      try {
        await tickRef.current();
      } catch {
        // Swallow — the caller's `tick` is expected to surface its own
        // errors via state. Letting the poll loop keep going avoids a
        // single transient failure stopping the page from ever updating.
      }
      if (cancelled) return;
      const delay = delayRef.current();
      if (delay === null) return;
      timer = setTimeout(run, delay);
    }

    function onVisibilityChange(): void {
      if (document.visibilityState !== "visible") return;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      void run();
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    void run();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** Common shape of poll delays so pages stay consistent. */
export const POLL = {
  /** Page actively shows in-flight work — fast refresh. */
  ACTIVE_MS: 5_000,
  /** Dashboard / list view with running rows — moderate refresh. */
  LIST_ACTIVE_MS: 10_000,
  /** Everything visible is terminal — slow safety refresh for UI staleness. */
  IDLE_MS: 60_000,
} as const;
